// Iter 570 — Antrean Shopee API: pagination backend, kategori produk, pencarian, urutan, urgensi global.
//   Claim tetap memakai endpoint & alur lama.
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Copy, Clock, ShoppingBag, CheckCircle2, AlertCircle, RefreshCw, Download, ExternalLink, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import "./shopee_mockup_queue.css";
import { ChannelTag, TokopediaAttachments, useChannel, getChannel } from "./channel";
import { TokopediaDeadline } from "./tokopedia_deadline";
import { QueueFilters } from "./shopee_queue_filters";
import { DesignerHistoryChip } from "./designer_history_chip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const chName = () => (getChannel() === "tokopedia" ? "Tokopedia" : "Shopee");
const PAGE_SIZE = 20;
const CAT_LABEL = {
  polo_pe: "Polo PE", polo_cvc: "Polo CVC", kemeja_kombinasi: "Kemeja Kombinasi", pdh_pdl: "PDH / PDL", kaos: "Kaos", rompi: "Rompi",
  jaket: "Jaket", jersey: "Jersey", workshirt: "Workshirt", biaya: "Biaya / Layanan", lainnya: "Perlu Klasifikasi",
};
const TECH_LABEL = { bordir: "Bordir", sablon_dtf: "Sablon/DTF", sublim: "Sublim" };
const techKey = (me) => `tamfaris.shopeeQueue.technique.${me?.id || me?.name || "anon"}`;
const URG_CLS = { red: "is-overdue", yellow: "is-warn", normal: "is-ok", unknown: "", none: "" };
const STATE_LABEL = { mockup_selesai: "Mockup selesai", fix_belum_gudang: "FIX · belum ke Gudang", dikirim_gudang: "Sudah dikirim ke Gudang",
  sudah_dikirim: "Sudah dikirim (Shopee)", batal: "Pekerjaan batal", tanpa_pekerjaan: "Tanpa pekerjaan mockup" };

const WIB = "Asia/Jakarta";
const MIN_VALID_TS = Date.UTC(2015, 0, 1); // epoch 0 / nilai kosong Shopee → tidak valid

const STATUS_LABEL = {
  to_ship: "Perlu Dikirim",
  ready_to_ship: "Siap Dikirim",
  processed: "Sedang Diproses",
  shipped: "Sudah Dikirim",
  completed: "Selesai",
  cancelling: "Pembatalan Diproses",
  in_cancel: "Pembatalan Diproses",
  cancelled: "Dibatalkan",
  pending_payment: "Belum Dibayar",
  to_return: "Pengembalian",
};

function validDeadlineMs(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) || t < MIN_VALID_TS ? null : t;
}

function fmtDeadline(iso) {
  const t = validDeadlineMs(iso);
  if (t === null) return null;
  return `${new Date(t).toLocaleString("id-ID", { timeZone: WIB, day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} WIB`;
}

function deadlineInfo(iso) {
  const t = validDeadlineMs(iso);
  if (t === null) return { cls: "", label: "Belum ada" };
  const diffH = Math.floor(Math.abs(t - Date.now()) / 3_600_000);
  const d = Math.floor(diffH / 24), h = diffH % 24;
  const span = d > 0 ? `${d} hr ${h} jam` : `${h} jam`;
  if (t < Date.now()) return { cls: "is-overdue", label: `Lewat ${span}` };
  return { cls: "", label: `Sisa ${span}` };
}

function fmtDate(iso) {
  const t = validDeadlineMs(iso);
  return t === null ? "—" : new Date(t).toLocaleString("id-ID", { timeZone: WIB, day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " WIB";
}

// Chip "Perlu Klasifikasi" disembunyikan bila pesanan sudah punya pekerjaan / sudah ke Gudang (tampilan saja).
export const hideUnknownCat = (o) => !!o?.claim_shopee_job_id || ["dikirim_gudang", "sudah_dikirim"].includes(o?.mockup_state);

function CatChips({ cats, hideUnknown }) {
  return (cats || []).filter(c => !(hideUnknown && c === "lainnya")).map(c => (
    <span key={c} className={`sq-cat-chip${c === "biaya" ? " is-fee" : c === "lainnya" ? " is-unknown" : ""}`}>{CAT_LABEL[c] || c}</span>
  ));
}

async function copyText(text, label) {
  if (!text) return;
  const v = String(text);
  try {
    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(v);
    else {
      const ta = Object.assign(document.createElement("textarea"), { value: v });
      ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand("copy"); ta.remove();
      if (!ok) throw new Error("copy");
    }
    toast.success(`${label} disalin`);
  } catch { toast.error(`Gagal menyalin ${label.toLowerCase()} — tekan lama nomor untuk menyalin manual`); }
}

// Ikon salin di dalam ringkasan kartu (yang berupa tombol) → span role=button agar tidak bersarang & tidak membuka kartu.
function InlineCopy({ text, testid }) {
  const run = (e) => { e.stopPropagation(); e.preventDefault(); copyText(text, "Nomor pesanan"); };
  return <span role="button" tabIndex={0} className="sq-copy-btn sq-copy-inline" data-testid={testid}
    title="Salin nomor pesanan" aria-label="Salin nomor pesanan" onClick={run}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") run(e); }}><Copy size={14}/></span>;
}

function CopyIcon({ text, label, testid }) {
  if (!text) return null;
  return (
    <button type="button" className="sq-copy-btn" data-testid={testid}
      title={`Salin ${label}`} aria-label={`Salin ${label}`}
      onClick={(e) => { e.stopPropagation(); copyText(text, label); }}>
      <Copy size={12}/>
    </button>
  );
}

function CardDetail({ order, buyer, cust, items, isDesigner, busy, onClaim }) {
  const oid = order.external_order_id;
  const deadlineTxt = fmtDeadline(order.ship_by_date_iso);
  const ic = order.item_categories || [];
  const combinedCopy = [
    `Pesanan: ${oid}`,
    buyer ? `Username: ${buyer}` : null,
    cust ? `Penerima: ${cust}` : null,
    `Batas Kirim: ${deadlineTxt || "belum tersedia"}`,
    ...items.map(it => `- ${it.name}${(it.variation || "").trim() ? ` (${it.variation})` : ""} x${it.quantity}`),
  ].filter(Boolean).join("\n");
  return (
    <div className="sq-detail" data-testid={`mockup-queue-detail-${oid}`}>
      <div className="sq-info-rows">
        <div className="sq-info-row">
          <span className="sq-info-label">Pesanan</span>
          <span className="sq-info-value">{oid}</span>
          <CopyIcon text={oid} label="Nomor Pesanan" testid={`mockup-queue-copy-order-${oid}`}/>
        </div>
        {buyer && (
          <div className="sq-info-row">
            <span className="sq-info-label">Username</span>
            <span className="sq-info-value" data-testid={`mockup-queue-buyer-${oid}`}>{buyer}</span>
          </div>
        )}
        {cust && (
          <div className="sq-info-row">
            <span className="sq-info-label">Penerima</span>
            <span className="sq-info-value" data-testid={`mockup-queue-recipient-${oid}`}>{cust}</span>
          </div>
        )}
        <div className="sq-info-row">
          <span className="sq-info-label">Tgl pesanan</span>
          <span className="sq-info-value" data-testid={`mockup-queue-created-${oid}`}>{fmtDate(order.order_created_at)}</span>
        </div>
        <div className="sq-info-row">
          <span className="sq-info-label">Batas kirim</span>
          <span className={`sq-info-value${deadlineTxt ? "" : " is-empty"}`} data-testid={`mockup-queue-deadline-${oid}`}>
            {deadlineTxt || "Batas kirim belum tersedia"}
          </span>
        </div>
        <div className="sq-info-row">
          <span className="sq-info-label">Status</span>
          <span className="sq-status-chip" data-testid={`mockup-queue-status-${oid}`} title={order.status_raw || order.status}>
            {STATUS_LABEL[order.status] || order.status_raw || order.status}
          </span>
        </div>
      </div>
      {items.length > 0 && (
        <ul className="sq-items" data-testid={`mockup-queue-items-${oid}`}>
          {items.map((it, i) => (
            <li key={i} className="sq-item" data-testid={`mockup-queue-item-${oid}-${i}`} style={order.item_mockup?.[i] === false ? { opacity: 0.55 } : undefined}>
              <span className="sq-item-name">
                {ic[i]?.category === "biaya"
                  ? <span className="sq-cat-chip is-fee">{ic[i].fee_label || "Biaya"}</span>
                  : ic[i]?.category === "lainnya" && hideUnknownCat(order) ? null
                  : <span className={`sq-cat-chip${ic[i]?.category === "lainnya" ? " is-unknown" : ""}`}>{CAT_LABEL[ic[i]?.category] || "?"}</span>}
                {" "}{it.name}
                {(it.variation || "").trim() && <span className="sq-item-variant"> · {it.variation}</span>}
                {(it.model_sku || it.sku) && <span className="sq-item-variant"> · SKU {it.model_sku || it.sku}</span>}
              </span>
              {order.item_mockup?.[i] === false && <span className="sq-cat-chip is-fee" data-testid={`mockup-queue-item-nomockup-${oid}-${i}`}>Tidak perlu mockup</span>}
              <span className="sq-item-qty">×{it.quantity}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="sq-footer">
        <button type="button" className="sq-btn-secondary"
          onClick={() => copyText(combinedCopy, "Data pesanan")}
          data-testid={`mockup-queue-copy-all-${oid}`}>
          <Copy size={13}/> Salin Data Lengkap
        </button>
        {isDesigner ? (
          <button type="button" className="sq-btn-primary" disabled={busy}
            onClick={() => onClaim(order)}
            data-testid={`mockup-queue-claim-btn-${oid}`}>
            <Download size={15}/> {busy ? "Mengambil…" : "Ambil Pekerjaan"}
          </button>
        ) : (
          <span className="sq-footer-note">Hanya desainer {chName()}</span>
        )}
      </div>
    </div>
  );
}

// Tokopedia: detail produk langsung di kartu — dari data API tersimpan; baris biaya tidak dihitung sebagai pcs.
export function ItemLines({ order }) {
  const ic = order.item_categories || [];
  const rows = (order.items || []).map((it, i) => ({ ...it, fee: ic[i]?.category === "biaya" }));
  const goods = rows.filter(r => !r.fee);
  const fees = rows.filter(r => r.fee);
  if (!goods.length) return null;
  const oid = order.external_order_id;
  const oneName = goods.every(g => g.name === goods[0].name);
  const total = goods.reduce((a, g) => a + (Number(g.quantity) || 0), 0);
  return (
    <span className="sq-lines" data-testid={`mockup-queue-lines-${oid}`}>
      {oneName && <span className="sq-line-name">{goods[0].name}</span>}
      {goods.map((g, i) => (
        <span key={i} className="sq-line" data-testid={`mockup-queue-line-${oid}-${i}`}>
          <span className="sq-line-var">{oneName ? "" : `${g.name} · `}{(g.variation || "").trim() || "Tanpa variasi"}</span>
          <span className="sq-line-qty">{g.quantity} pcs</span>
        </span>
      ))}
      {goods.length > 1 && <span className="sq-line sq-line-total"><span>Total</span><span className="sq-line-qty" data-testid={`mockup-queue-total-${oid}`}>{total} pcs</span></span>}
      {fees.length > 0 && <span className="sq-line-fee">+ {fees.length} baris biaya setting (tidak dihitung pcs)</span>}
    </span>
  );
}

function CardSummary({ order, onToggle, expanded, statusText }) {
  const buyer = (order.buyer_username || order.customer_name || "").trim();
  const dl = deadlineInfo(order.ship_by_date_iso);
  const oid = order.external_order_id;
  const name = order.fee_only ? "Hanya biaya / layanan" : (order.primary_name || "—");
  return (
    <button type="button" className="sq-card-summary" onClick={onToggle}
      aria-expanded={expanded} data-testid={`mockup-queue-card-toggle-${oid}`}>
      <span className="sq-sum-row">
        <span className="sq-order-no" data-testid={`mockup-queue-order-${oid}`}>{oid}</span>
        <InlineCopy text={oid} testid={`mockup-queue-copy-order-${oid}`}/>
        <span className={`sq-dl-chip ${URG_CLS[order.urgency] || ""}`} data-testid={`mockup-queue-urgency-${oid}`}>
          <Clock size={11}/>{order.designer_action === false ? ((order.mockup_state === "sudah_dikirim" ? `Sudah dikirim (${chName()})` : STATE_LABEL[order.mockup_state]) || "Tidak perlu tindakan") : dl.label}
        </span>
        <ChevronDown size={16} className="sq-chevron"/>
      </span>
      <span className="sq-sum-line">{buyer || "Identitas tidak tersedia"} · {statusText}
        {order.designer_history && <> <DesignerHistoryChip history={order.designer_history} testid={`mockup-queue-designer-history-${oid}`}/></>}</span>
      <span className="sq-sum-line sq-sum-cats">
        {(order.techniques || []).length
          ? order.techniques.map(t => <span key={t} className="sq-cat-chip is-tech">{TECH_LABEL[t] || t}</span>)
          : <span className="sq-cat-chip is-unknown">Teknik?</span>}
        <CatChips cats={order.categories} hideUnknown={hideUnknownCat(order)}/>
        {getChannel() !== "tokopedia" && <span className="is-faint"> {name}{(order.pcs_mockup ?? order.pcs) ? ` · ${order.pcs_mockup ?? order.pcs} pcs perlu mockup` : ""}</span>}
      </span>
      {getChannel() === "tokopedia" && <ItemLines order={order}/>}
    </button>
  );
}

function UnclaimedCard({ order, expanded, onToggle, ...rest }) {
  const buyer = (order.buyer_username || "").trim();
  const cust = (order.customer_name || "").trim();
  return (
    <li className={`sq-card ${URG_CLS[order.urgency] || ""}${expanded ? " is-expanded" : ""}`}
      data-testid={`mockup-queue-unclaimed-${order.external_order_id}`}>
      <div className="sq-card-head">
        <ChannelTag/>
        <TokopediaAttachments orderNumber={order.external_order_id}/>
        <TokopediaDeadline orderNumber={order.external_order_id}/>
        <CardSummary order={order} onToggle={onToggle} expanded={expanded} statusText="Belum Diambil"/>
        {rest.isDesigner && !expanded && (
          <button type="button" className="sq-quick-claim" disabled={rest.busy}
            onClick={() => rest.onClaim(order)} title="Ambil Pekerjaan"
            data-testid={`mockup-queue-quick-claim-${order.external_order_id}`}>
            <Download size={14}/>{rest.busy ? "…" : "Ambil"}
          </button>
        )}
      </div>
      {expanded && <CardDetail order={order} buyer={buyer} cust={cust} items={order.items || []} {...rest}/>}
    </li>
  );
}

function InSystemCard({ order, expanded, onToggle, onOpenExisting }) {
  const oid = order.external_order_id;
  return (
    <li className={`sq-card is-in-system${expanded ? " is-expanded" : ""}`} data-testid={`mockup-queue-insystem-${oid}`}>
      <CardSummary order={order} onToggle={onToggle} expanded={expanded}
        statusText={order.claim_designer_name ? `Sudah Ada · Desainer ${order.claim_designer_name}` : "Sudah Ada"}/>
      {expanded && order.claim_shopee_job_id && (
        <div className="sq-detail">
          <button type="button" className="sq-btn-secondary"
            onClick={() => onOpenExisting(order.claim_shopee_job_id)}
            data-testid={`mockup-queue-open-existing-${oid}`}>
            <ExternalLink size={13}/> Buka Pekerjaan
          </button>
        </div>
      )}
    </li>
  );
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

const isDesktop = () => typeof window !== "undefined" && window.matchMedia("(min-width: 1280px)").matches;

export function ShopeeMockupQueuePanel({ me, onJobCreated, onOpenExisting, defaultOpenDesktop = false }) {
  const channel = useChannel();
  const [open, setOpen] = useState(() => defaultOpenDesktop && isDesktop());
  const [tab, setTab] = useState("unclaimed");
  const [category, setCategory] = useState("all");
  // Preferensi teknik per desainer (disimpan di perangkat ini; tidak mengubah hak akses/claim).
  const [technique, setTechniqueState] = useState(() => {
    try { return localStorage.getItem(techKey(me)) || "all"; } catch { return "all"; }
  });
  useEffect(() => {  // akun desainer baru dimuat → pakai preferensinya
    try { setTechniqueState(localStorage.getItem(techKey(me)) || "all"); } catch { /* abaikan */ }
  }, [me?.id, me?.name]); // eslint-disable-line react-hooks/exhaustive-deps
  const setTechnique = (t) => {
    setTechniqueState(t);
    try { localStorage.setItem(techKey(me), t); } catch { /* abaikan */ }
  };
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput.trim(), 300);
  const [sort, setSort] = useState("deadline");
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [expandedId, setExpandedId] = useState("");
  const reqId = useRef(0);

  const isDesigner = me?.level === "designer";

  const load = useCallback(async (skip = 0) => {
    const id = ++reqId.current;
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/shopee/mockup-queue/list`, {
        params: { tab, category, technique, q, sort, urgent_only: urgentOnly, skip, limit: PAGE_SIZE }, withCredentials: true });
      if (id !== reqId.current) return;
      setMeta(r.data);
      setItems(prev => (skip ? [...prev, ...r.data.items] : r.data.items));
    } catch (e) {
      if (id === reqId.current) setErr(e?.response?.data?.detail || `Gagal memuat antrean ${chName()}`);
    } finally { if (id === reqId.current) setLoading(false); }
  }, [tab, category, technique, q, sort, urgentOnly]);

  useEffect(() => { setExpandedId(""); load(0); }, [load]);
  useEffect(() => { const h = () => load(0); window.addEventListener("tkp-deadline-changed", h); return () => window.removeEventListener("tkp-deadline-changed", h); }, [load]);

  const handleClaim = async (order) => {
    if (busyId) return;
    setBusyId(order.id);
    try {
      const r = await axios.post(
        `${API}/shopee/mockup-queue/claim/${encodeURIComponent(order.id)}`,
        {}, { withCredentials: true }
      );
      toast.success(`Pekerjaan ${order.external_order_id} berhasil diambil`);
      setExpandedId("");
      load(0);
      if (r.data?.job?.id && onJobCreated) onJobCreated(r.data.job);
    } catch (e) {
      const status = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (status === 409) {
        toast.error(typeof d === "string" ? d : (d?.detail || "Pekerjaan sudah tidak dapat diambil."));
        load(0);
      } else {
        toast.error(typeof d === "string" ? d : (d?.detail || "Gagal mengambil pekerjaan"));
      }
    } finally { setBusyId(""); }
  };

  const totals = meta?.tab_totals || {};
  const total = meta?.total ?? 0;
  const toggle = (id) => setExpandedId(x => (x === id ? "" : id));

  return (
    <section className={`sq-root${open ? " is-open" : ""}`} data-testid="mockup-queue-panel">
      <div className="sq-header">
        <button type="button" className="sq-header-toggle" onClick={() => setOpen(v => !v)}
          aria-expanded={open} data-testid="mockup-queue-panel-toggle">
          <ShoppingBag size={16}/>
          <span className="sq-title" data-testid="mockup-queue-title">{channel === "tokopedia" ? "Antrean Tokopedia" : "Antrean Shopee"}</span>
          <span className="sq-counts">
            <span className="sq-count is-main" data-testid="mockup-queue-unclaimed-count">{totals.unclaimed ?? 0} belum</span>
            {(meta?.urgency_counts?.red || 0) > 0 && tab === "unclaimed" && (
              <span className="sq-count is-red" data-testid="mockup-queue-header-urgent">{meta.urgency_counts.red} mendesak</span>
            )}
            <span className="sq-count" data-testid="mockup-queue-insystem-count">{totals.in_system ?? 0} di sistem</span>
          </span>
          <ChevronDown size={18} className="sq-chevron"/>
        </button>
        <button type="button" className="sq-icon-btn" onClick={() => load(0)}
          data-testid="mockup-queue-refresh" disabled={loading}
          aria-label="Muat ulang antrean" title="Muat ulang">
          <RefreshCw size={13} className={loading ? "spin" : ""}/>
        </button>
      </div>

      {open && (
        <div className="sq-body" data-testid="mockup-queue-body">
          <div className="sq-tabs">
            <button type="button" className={`sq-tab${tab === "unclaimed" ? " is-active" : ""}`}
              onClick={() => setTab("unclaimed")} data-testid="mockup-queue-tab-unclaimed">
              Belum Diambil <span className="sq-tab-badge">{totals.unclaimed ?? 0}</span>
            </button>
            <button type="button" className={`sq-tab${tab === "in_system" ? " is-active" : ""}`}
              onClick={() => setTab("in_system")} data-testid="mockup-queue-tab-insystem">
              Sudah Ada <span className="sq-tab-badge">{totals.in_system ?? 0}</span>
            </button>
          </div>

          <QueueFilters meta={meta} category={category} setCategory={setCategory} qInput={qInput} setQInput={setQInput}
            sort={sort} setSort={setSort} urgentOnly={urgentOnly} setUrgentOnly={setUrgentOnly}
            technique={technique} setTechnique={setTechnique}/>

          {err && (
            <div className="sq-error" data-testid="mockup-queue-error">
              <AlertCircle size={14}/>
              <span className="sq-error-msg">{err}</span>
              <button type="button" className="sq-icon-btn" onClick={() => load(0)} data-testid="mockup-queue-retry" title="Coba lagi">
                <RefreshCw size={13}/>
              </button>
            </div>
          )}

          {!err && meta && (
            <p className="sq-note" data-testid="mockup-queue-result-count">
              Menampilkan {items.length} dari {total} pesanan{urgentOnly ? " mendesak" : ""}.
            </p>
          )}
          {!err && meta && tab === "in_system" && meta.state_counts && (
            <p className="sq-note" data-testid="mockup-queue-state-counts">
              {meta.state_counts.perlu_mockup || 0} masih perlu mockup · {meta.state_counts.fix_belum_gudang || 0} FIX belum ke Gudang · {meta.state_counts.dikirim_gudang || 0} sudah ke Gudang · {meta.state_counts.sudah_dikirim || 0} sudah dikirim {chName()}
              {(meta.state_counts.batal || 0) + (meta.state_counts.tanpa_pekerjaan || 0) > 0 ? ` · ${(meta.state_counts.batal || 0) + (meta.state_counts.tanpa_pekerjaan || 0)} batal/tanpa pekerjaan` : ""}
            </p>
          )}

          {loading && items.length === 0 && (
            <div className="sq-info" data-testid="mockup-queue-loading">Memuat antrean {chName()}…</div>
          )}

          {!loading && !err && items.length === 0 && (
            <div className="sq-info" data-testid="mockup-queue-empty">
              {q || category !== "all" || technique !== "all" || urgentOnly
                ? "Tidak ada pesanan yang cocok dengan filter."
                : tab === "unclaimed"
                  ? <><CheckCircle2 size={14} className="sq-ok-icon"/> Tidak ada pesanan {chName()} yang perlu diambil.</>
                  : `Belum ada pesanan ${chName()} yang terkait pekerjaan sistem.`}
            </div>
          )}

          {items.length > 0 && (
            <ul className="sq-list" data-testid={`mockup-queue-list-${tab}`}>
              {items.map(o => (tab === "unclaimed"
                ? <UnclaimedCard key={o.id} order={o} isDesigner={isDesigner} expanded={expandedId === o.id}
                    onToggle={() => toggle(o.id)} busy={busyId === o.id} onClaim={handleClaim}/>
                : <InSystemCard key={o.id} order={o} expanded={expandedId === o.id}
                    onToggle={() => toggle(o.id)} onOpenExisting={onOpenExisting}/>))}
            </ul>
          )}

          {items.length < total && (
            <button type="button" className="sq-btn-secondary sq-load-more" disabled={loading}
              onClick={() => load(items.length)} data-testid="mockup-queue-load-more">
              Muat Berikutnya ({total - items.length} lagi)
            </button>
          )}
        </div>
      )}
    </section>
  );
}
